/**
 * MifeLife v2 — Dashboard layout (sidebar + outlet) and the 5 inner pages.
 *
 * - V2DashboardLayout: wraps /app/* with a sidebar (Home, Calendar, Goals,
 *   My Mentor, AI Assistant, Settings) and the top energy-pill.
 * - V2DashboardHome: dashboard landing with 4 widgets.
 * - V2CalendarPage: weekly view of mentor sessions.
 * - V2GoalsPage: checklist of roadmap goals.
 * - V2AssistantPage: chat interface.
 * - V2MyMentorPage: list of upcoming/past sessions.
 * - V2SettingsPage: minimal stub.
 */
import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Home, Calendar, Target, User, MessageSquare, Settings, Sparkles, LogOut,
  Loader2, Send, ChevronRight, CheckCircle2, Circle, ExternalLink, Shield, ArrowLeft,
  Video,
} from 'lucide-react';
import { apiRequest, getToken, logout as clearAuth } from '../../lib/utils';
import { useNoIndex } from '../../hooks/useNoIndex';

// Mirrors backend _HARDCODED_ADMINS (v2_modules/admin_v2.py). Adding an email
// here only changes UI visibility; backend still gates every /api/v2/admin/*
// call, so this is safe to keep client-side.
const ADMIN_EMAILS = new Set(['hak.dalmis@gmail.com', 'demo_founder@mifelife.com']);
const isAdmin = (u) => !!u && (u.role === 'admin' || ADMIN_EMAILS.has((u.email || '').toLowerCase()));

const SIDEBAR = [
  { to: '/app',             label: 'Dashboard', icon: Home,    end: true,  testid: 'v2-side-home' },
  { to: '/app/takvim',      label: 'Takvim',    icon: Calendar, testid: 'v2-side-takvim' },
  { to: '/app/hedefler',    label: 'Hedefler',  icon: Target,   testid: 'v2-side-hedefler' },
  { to: '/app/mentorum',    label: 'Mentörüm',  icon: User,     testid: 'v2-side-mentorum' },
  { to: '/app/ai-asistan',  label: 'AI Asistan',icon: MessageSquare, testid: 'v2-side-asistan' },
  { to: '/app/ayarlar',     label: 'Ayarlar',   icon: Settings, testid: 'v2-side-ayarlar' },
];

export function V2DashboardLayout() {
  useNoIndex();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!getToken()) { nav('/giris'); return; }
    apiRequest('/auth/me')
      .then((u) => { setMe(u); setReady(true); })
      .catch(() => { clearAuth(); nav('/giris'); });
  }, [nav]);

  const logout = () => { clearAuth(); nav('/'); };

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white antialiased flex" data-testid="v2-dashboard-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 flex-col border-r border-white/[0.06] bg-[#0a0a14] sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 px-6 h-16 border-b border-white/[0.04]">
          <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-cyan-400 flex items-center justify-center"><Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2.4} /></span>
          <span className="text-base font-semibold tracking-tight">MifeLife</span>
        </Link>
        <nav className="flex-1 px-3 py-5 space-y-1">
          {SIDEBAR.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition ${isActive ? 'bg-violet-500/15 text-white border border-violet-400/30' : 'text-slate-400 hover:bg-white/[0.04] hover:text-white border border-transparent'}`}
            >
              <it.icon className="w-4 h-4" strokeWidth={1.8} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/[0.04]">
          {isAdmin(me) && (
            <Link
              to="/admin"
              className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-sm text-violet-300 hover:bg-violet-500/10 hover:text-violet-200 border border-violet-400/20 transition"
              data-testid="v2-side-admin"
            >
              <Shield className="w-4 h-4" strokeWidth={1.8} /> Admin Panel
            </Link>
          )}
          <Link
            to="/"
            className="w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-sm text-slate-400 hover:bg-white/[0.04] hover:text-white transition"
            data-testid="v2-side-landing"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.8} /> Ana sayfa
          </Link>
          <div className="px-3 mb-2 text-[11px] text-slate-500 truncate">{me?.email || '—'}</div>
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-white/[0.04] hover:text-white transition" data-testid="v2-side-logout">
            <LogOut className="w-4 h-4" /> Çıkış
          </button>
        </div>
      </aside>

      {/* Outlet */}
      <main className="flex-1 min-h-screen overflow-x-hidden">
        {ready ? <Outlet context={{ me }} /> : null}
      </main>
    </div>
  );
}

// ============================================================================
// DASHBOARD HOME
// ============================================================================
export function V2DashboardHome() {
  const [me, setMe] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [goals, setGoals] = useState([]);
  const [energy, setEnergy] = useState('orta');

  useEffect(() => {
    const tzOffset = -new Date().getTimezoneOffset(); // minutes east of UTC
    apiRequest('/auth/me').then(setMe).catch(() => {});
    apiRequest('/v2/analysis/latest').then(setAnalysis).catch(() => {});
    apiRequest(`/v2/briefing/today?tz_offset_min=${tzOffset}`).then(setBriefing).catch(() => {});
    apiRequest('/v2/sessions/upcoming').then((r) => setSessions(r?.items || [])).catch(() => {});
    apiRequest('/v2/goals/').then((r) => setGoals(r?.items || [])).catch(() => {});
    document.title = 'Dashboard · MifeLife';
  }, []);

  const completionPct = (() => {
    if (!goals.length) return 0;
    const done = goals.filter((g) => g.completed).length;
    return Math.round((done / goals.length) * 100);
  })();
  const next = sessions[0];
  const firstName = (me?.name || me?.email || 'oradayım').split(/[\s@]/)[0];

  return (
    <div className="p-6 sm:p-10" data-testid="v2-dash-home">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">İyi günler, {firstName}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-[12px]">
          <span className="text-slate-500 uppercase tracking-[0.18em]">Bugün enerjin nasıl?</span>
          {['düşük', 'orta', 'yüksek'].map((e) => (
            <button
              key={e}
              onClick={() => setEnergy(e)}
              className={`px-3 py-1 rounded-full border transition ${energy === e ? 'bg-violet-500/15 border-violet-400/40 text-white' : 'border-white/10 bg-white/[0.02] text-slate-400 hover:text-white'}`}
              data-testid={`v2-energy-${e}`}
            >{e}</button>
          ))}
        </div>
      </header>

      <div className="grid lg:grid-cols-2 gap-4">
        <Widget title={briefing?.slot === 'midday' ? 'Gün ortası' : 'Sabah brifing'} testid="v2-widget-brief">
          {briefing ? (
            <p className="text-[14px] text-slate-200 leading-relaxed whitespace-pre-line">{briefing.brief}</p>
          ) : analysis ? (
            <p className="text-[14px] text-slate-200 leading-relaxed">{analysis.diagnosis}</p>
          ) : (
            <div className="text-sm text-slate-400">Henüz analiz yapılmadı. <Link to="/analiz" className="text-violet-300 underline">10 soruyu yanıtla →</Link></div>
          )}
        </Widget>

        <Widget title="Yaklaşan seans" testid="v2-widget-next-session">
          {next ? (
            <UpcomingSessionTile session={next} />
          ) : (
            <div className="text-sm text-slate-400">Henüz seansın yok. <Link to="/mentorler" className="text-violet-300 underline">Mentör seç →</Link></div>
          )}
        </Widget>

        <Widget title="Hedef ilerleme" testid="v2-widget-progress">
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-semibold tabular-nums">{completionPct}%</span>
            <span className="text-xs text-slate-500">{goals.length} hedef</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-violet-500 to-cyan-400" style={{ width: `${completionPct}%` }} />
          </div>
          <Link to="/app/hedefler" className="mt-4 text-xs text-violet-300 inline-flex items-center gap-1">Tüm hedefler <ChevronRight className="w-3 h-3" /></Link>
        </Widget>

        <Widget title="AI Asistan" testid="v2-widget-assistant">
          <div className="text-sm text-slate-300 mb-3">Bugün ne yapmalısın? AI Asistana sor →</div>
          <Link to="/app/ai-asistan" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition">Sohbete başla</Link>
        </Widget>
      </div>
    </div>
  );
}

const Widget = ({ title, children, testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-4">{title}</div>
    {children}
  </div>
);

const UpcomingSessionTile = ({ session }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(i); }, []);
  const target = new Date(session.scheduled_at).getTime();
  const minsUntil = Math.round((target - now) / 60000);
  const canJoin = minsUntil <= 15 && minsUntil > -(session.duration_min || 60);
  const label = minsUntil <= 0 ? 'Şu an' : (minsUntil < 60 ? `${minsUntil} dk içinde` : `${Math.floor(minsUntil / 60)} sa ${minsUntil % 60} dk`);
  return (
    <div className="flex items-center gap-3" data-testid="v2-upcoming-tile">
      <img src={session.mentor_photo_url} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{session.mentor_name}</div>
        <div className="text-xs text-slate-400">{new Date(session.scheduled_at).toLocaleString('tr-TR')}</div>
        <div className="text-[11px] text-violet-300 mt-0.5">{label}</div>
      </div>
      {canJoin ? (
        <Link
          to={`/app/seans/${session.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-500 text-slate-900 text-xs font-semibold hover:bg-emerald-400 transition"
          data-testid="v2-upcoming-join"
        >
          <Video className="w-3.5 h-3.5" /> Görüşmeye katıl
        </Link>
      ) : (
        <Link
          to={`/app/seans/${session.id}`}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 bg-white/[0.04] text-slate-300 text-xs font-medium hover:bg-white/[0.08] transition"
          data-testid="v2-upcoming-detail"
        >
          Detay
        </Link>
      )}
    </div>
  );
};

// ============================================================================
// CALENDAR (weekly grid + AI-suggested slots + draggable goals)
// ============================================================================
export function V2CalendarPage() {
  const [sessions, setSessions] = useState([]);
  const [goals, setGoals] = useState([]);
  // placements: { [`${dayKey}_${hour}`]: goalId | 'work' }
  const [placements, setPlacements] = useState(() => {
    try { return JSON.parse(localStorage.getItem('v2_cal_placements') || '{}'); } catch { return {}; }
  });
  const [dragGoalId, setDragGoalId] = useState(null);

  useEffect(() => {
    apiRequest('/v2/sessions/upcoming').then((r) => setSessions(r?.items || [])).catch(() => {});
    apiRequest('/v2/goals/').then((r) => setGoals((r?.items || []).filter((g) => !g.completed))).catch(() => {});
    document.title = 'Takvim · MifeLife';
  }, []);

  // Persist placements locally — Sprint 3 will push to backend.
  useEffect(() => { try { localStorage.setItem('v2_cal_placements', JSON.stringify(placements)); } catch {} }, [placements]);

  // Week starts Monday.
  const today = new Date();
  const mon = new Date(today); mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; });
  const hours = [9, 10, 11, 14, 15, 16, 19, 20];
  const DAY_SHORT = ['Pzt','Sal','Çar','Per','Cum','Cmt','Paz'];

  const sessionAt = (d, h) => sessions.find((s) => {
    const sd = new Date(s.scheduled_at);
    return sd.toDateString() === d.toDateString() && sd.getHours() === h;
  });
  const aiSlot = (d, h) => (d.getDay() === 1 || d.getDay() === 3) && h === 10; // Mon/Wed 10:00

  const onDrop = (d, h) => {
    if (!dragGoalId) return;
    const key = `${d.toDateString()}_${h}`;
    setPlacements((p) => ({ ...p, [key]: dragGoalId }));
    setDragGoalId(null);
  };
  const clearSlot = (key) => setPlacements((p) => { const { [key]: _, ...rest } = p; return rest; });
  const goalById = (id) => goals.find((g) => g.id === id);

  return (
    <div className="p-6 sm:p-10" data-testid="v2-calendar-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Takvim</h1>
      <p className="text-sm text-slate-400 mb-6">Hedefleri sürükle, takvime bırak. Mentör seansların ve AI önerileri kilitli.</p>

      <div className="grid lg:grid-cols-[200px_1fr] gap-6">
        {/* Goal pool */}
        <aside className="space-y-2" data-testid="v2-calendar-goalpool">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Sürüklenebilir hedefler</div>
          {goals.length === 0 && <div className="text-xs text-slate-500">Aktif hedef yok. <Link to="/app/hedefler" className="text-violet-300 underline">Yükle →</Link></div>}
          {goals.map((g) => (
            <div
              key={g.id}
              draggable
              onDragStart={() => setDragGoalId(g.id)}
              onDragEnd={() => setDragGoalId(null)}
              className="rounded-lg border border-white/10 bg-white/[0.04] p-3 cursor-grab active:cursor-grabbing text-[12px] hover:bg-white/[0.07] transition"
              data-testid={`v2-cal-goal-${g.id}`}
            >
              <div className="text-slate-200 leading-snug">{g.title}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1.5">{g.category}</div>
            </div>
          ))}
        </aside>

        {/* Weekly grid */}
        <div className="overflow-x-auto" data-testid="v2-calendar-grid">
          <div className="min-w-[700px] grid grid-cols-[60px_repeat(7,minmax(0,1fr))] gap-1">
            <div></div>
            {days.map((d, i) => (
              <div key={i} className="text-center pb-3">
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{DAY_SHORT[i]}</div>
                <div className="text-sm font-semibold tabular-nums">{d.getDate()}</div>
              </div>
            ))}
            {hours.map((h) => (
              <React.Fragment key={h}>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 pr-2 text-right pt-2 tabular-nums">{h}:00</div>
                {days.map((d, i) => {
                  const key = `${d.toDateString()}_${h}`;
                  const ses = sessionAt(d, h);
                  const placed = placements[key];
                  const ai = aiSlot(d, h);
                  const goal = placed ? goalById(placed) : null;
                  return (
                    <div
                      key={`${i}-${h}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => !ses && onDrop(d, h)}
                      className={`min-h-[42px] rounded border text-[11px] p-1.5 transition ${
                        ses ? 'border-violet-400/60 bg-violet-500/25 cursor-not-allowed'
                            : goal ? 'border-emerald-400/40 bg-emerald-500/15'
                            : ai ? 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06] cursor-pointer'
                            : 'border-white/[0.06] bg-white/[0.01] hover:bg-white/[0.04]'
                      }`}
                      data-testid={`v2-cal-cell-${i}-${h}`}
                    >
                      {ses && <div className="text-white truncate">{ses.mentor_name}</div>}
                      {!ses && goal && (
                        <div className="flex items-start gap-1">
                          <span className="text-emerald-200 truncate flex-1">{goal.title}</span>
                          <button onClick={() => clearSlot(key)} className="text-emerald-300/60 hover:text-rose-300 leading-none">×</button>
                        </div>
                      )}
                      {!ses && !goal && ai && <span className="text-slate-500">AI: Çalış</span>}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 text-[11px] text-slate-500 flex flex-wrap gap-4">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-violet-500/40 border border-violet-400/60" /> Mentör seansı</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-400/40" /> Yerleştirilen hedef</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded bg-white/[0.03] border border-white/10" /> AI önerisi</span>
      </div>

      {sessions.length === 0 && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-sm text-slate-400">
          Henüz mentör seansın yok. <Link to="/mentorler" className="text-violet-300 underline">Mentör seç →</Link>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// GOALS
// ============================================================================
export function V2GoalsPage() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    apiRequest('/v2/goals/').then((r) => setGoals(r?.items || [])).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); document.title = 'Hedefler · MifeLife'; }, []);

  const seed = async () => {
    setLoading(true);
    try { await apiRequest('/v2/goals/from-roadmap', { method: 'POST' }); refresh(); }
    catch { setLoading(false); }
  };

  const toggle = async (g) => {
    await apiRequest(`/v2/goals/${g.id}`, { method: 'PATCH', body: JSON.stringify({ completed: !g.completed }) });
    refresh();
  };

  return (
    <div className="p-6 sm:p-10" data-testid="v2-goals-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Hedefler</h1>
          <p className="text-sm text-slate-400 mt-1">AI yol haritandan gelen 5 başlangıç görevini takvimine eklemek için doldur.</p>
        </div>
        <button onClick={seed} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg border border-white/15 bg-white/[0.04] text-sm text-slate-200 hover:bg-white/[0.08] transition" data-testid="v2-goals-seed">Yol haritamı yükle</button>
      </div>

      {loading ? <Loader2 className="w-6 h-6 text-violet-300 animate-spin mx-auto py-10" /> : goals.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center text-sm text-slate-400">Henüz hedef yok. Önce <Link to="/analiz" className="text-violet-300 underline">AI analizi</Link> yap, sonra "Yol haritamı yükle"ye bas.</div>
      ) : (
        <div className="space-y-2" data-testid="v2-goals-list">
          {goals.map((g) => (
            <button key={g.id} onClick={() => toggle(g)} className={`w-full text-left rounded-xl border p-4 flex gap-3 transition ${g.completed ? 'border-emerald-400/30 bg-emerald-500/[0.05]' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'}`} data-testid={`v2-goal-${g.id}`}>
              {g.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" /> : <Circle className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className={`text-[14px] ${g.completed ? 'text-slate-400 line-through' : 'text-white'}`}>{g.title}</div>
                {g.description && <div className="text-xs text-slate-500 mt-1">{g.description}</div>}
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-2">{g.category}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// AI ASSISTANT
// ============================================================================
export function V2AssistantPage() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = 'AI Asistan · MifeLife'; }, []);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || busy) return;
    const userMsg = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: userMsg, id: Date.now() }]);
    setBusy(true);
    try {
      const res = await apiRequest('/v2/assistant/chat', {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId, message: userMsg, language: 'tr' }),
      });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply, id: Date.now() + 1 }]);
    } catch (e2) {
      setMessages((m) => [...m, { role: 'assistant', content: '⚠️ Asistan şu anda erişilemez.', id: Date.now() + 1 }]);
    } finally {
      setBusy(false);
    }
  };

  const quick = (txt) => { setInput(txt); setTimeout(() => document.querySelector('[data-testid=v2-assistant-form]')?.requestSubmit?.(), 50); };

  return (
    <div className="flex flex-col h-screen" data-testid="v2-assistant-page">
      <div className="p-6 sm:p-10 border-b border-white/[0.06]">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">AI Asistan</h1>
        <p className="text-sm text-slate-400 mt-1">Skorlarına bağlı kişisel Chief of Staff.</p>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          {['Gün özetimi yap', 'Motive et', 'Hedef analizi yap', 'Mentör önersene'].map((t) => (
            <button key={t} onClick={() => quick(t)} className="px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] text-slate-300 hover:bg-white/[0.05] transition" data-testid={`v2-assistant-quick-${t.slice(0,3)}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 sm:px-10 py-6 space-y-3" data-testid="v2-assistant-feed">
        {messages.length === 0 && <div className="text-sm text-slate-500 text-center py-12">Sohbete başla — sol altta bir soru yaz.</div>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${m.role === 'user' ? 'bg-violet-500 text-white' : 'bg-white/[0.04] text-slate-100 border border-white/10'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="rounded-2xl px-4 py-3 bg-white/[0.04] border border-white/10"><Loader2 className="w-4 h-4 animate-spin text-violet-300" /></div></div>
        )}
      </div>

      <form onSubmit={send} className="p-4 sm:p-6 border-t border-white/[0.06] bg-[#0a0a14]" data-testid="v2-assistant-form">
        <div className="max-w-3xl mx-auto flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Sor…" className="flex-1 h-11 px-4 rounded-lg bg-white/[0.04] border border-white/10 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-400/40" data-testid="v2-assistant-input" />
          <button type="submit" disabled={busy || !input.trim()} className="h-11 px-5 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 disabled:opacity-30 transition flex items-center gap-1.5" data-testid="v2-assistant-send"><Send className="w-4 h-4" /></button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// MY MENTOR (sessions history)
// ============================================================================
export function V2MyMentorPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiRequest('/v2/sessions/history').then((r) => setItems(r?.items || [])).finally(() => setLoading(false));
    document.title = 'Mentörüm · MifeLife';
  }, []);

  return (
    <div className="p-6 sm:p-10" data-testid="v2-mentorum-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Mentörüm</h1>
      <p className="text-sm text-slate-400 mb-8">Tüm mentör seansların tek bir yerde.</p>
      {loading ? <Loader2 className="w-6 h-6 text-violet-300 animate-spin mx-auto py-10" /> : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-10 text-center">
          <div className="text-sm text-slate-400 mb-4">Henüz seans yok.</div>
          <Link to="/mentorler" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-violet-500 text-white text-sm font-medium hover:bg-violet-400 transition">Mentör seç</Link>
        </div>
      ) : (
        <div className="space-y-3" data-testid="v2-mentorum-list">
          {items.map((s) => (
            <div key={s.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex items-center gap-4">
              <img src={s.mentor_photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">{s.mentor_name}</div>
                <div className="text-xs text-slate-400">{new Date(s.scheduled_at).toLocaleString('tr-TR')}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">{s.status}</div>
              </div>
              {s.jitsi_link && (
                <Link to={`/app/seans/${s.id}`} className="text-xs text-violet-300 inline-flex items-center gap-1 hover:text-violet-200 transition" data-testid={`v2-mentorum-open-${s.id}`}>
                  <Video className="w-3 h-3" /> Seans sayfası
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SETTINGS
// ============================================================================
export function V2SettingsPage() {
  const [me, setMe] = useState(null);
  useEffect(() => {
    apiRequest('/auth/me').then(setMe).catch(() => {});
    document.title = 'Ayarlar · MifeLife';
  }, []);

  return (
    <div className="p-6 sm:p-10" data-testid="v2-settings-page">
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Ayarlar</h1>
      <p className="text-sm text-slate-400 mb-8">Hesap bilgilerin.</p>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 max-w-md space-y-3">
        <Row label="İsim" value={me?.name || '—'} />
        <Row label="E-posta" value={me?.email || '—'} />
        <Row label="Plan" value={(me?.subscription_tier || 'free').toUpperCase()} />
      </div>
    </div>
  );
}
const Row = ({ label, value }) => (
  <div className="flex items-baseline gap-3">
    <span className="w-24 text-[11px] uppercase tracking-[0.18em] text-slate-500">{label}</span>
    <span className="text-sm text-slate-200">{value}</span>
  </div>
);
